import {message} from "antd";
import {CreateProductFormValues} from "../reducers/create-product/types";
import {CreateProductApiResponse} from "../../../../react-query-hooks/products/create-product/types";
import {GetProductApiResponse} from "../../../../react-query-hooks/products/types";
import {useCreateProductContext} from "../context/create-product";
import {useDigitalProductMutations} from "../../../../features/digital-products";
import {DigitalProduct} from "../../../../features/digital-products/domain/digital-product.entity";
import {
  CreateDigitalProductInput,
  UpdateDigitalProductInput,
} from "../../../../features/digital-products/domain/digital-product.types";
import {toDigitalProductPricingInput} from "../../product-pricing";
import {
  toPurchaseLimitPayload,
  toPurchaseLimitUpdatePayload,
} from "../../../../components/offering-form/general/settings/purchase-limit-payload";

/**
 * Adapter: maps create wizard form values to the domain create input.
 */
function formValuesToCreateInput(
  values: CreateProductFormValues
): CreateDigitalProductInput {
  const redirectType =
    values.redirectAfterPurchase === "custom-url"
      ? "url"
      : values.redirectAfterPurchase ?? "none";

  return {
    name: values.name,
    description: values.description,
    ...toDigitalProductPricingInput(values),
    hideFromProfile: values.hideFromProfile,
    coverImage: values.coverImage || undefined,
    urlRedirect: redirectType === "url" ? values.url_redirect ?? undefined : undefined,
    redirectType,
    telegramGroupChatId:
      redirectType === "telegram" ? values.telegramGroupId ?? null : null,
    requestPhoneNumber: values.requestCustomerPhoneNumber,
    ...toPurchaseLimitPayload(values),
    bookingQuestions:
      values.bookingQuestions.length > 0
        ? values.bookingQuestions.map((q) => ({
            question: q.question,
            required: q.required,
            answerType: q.answerType,
            options:
              q.options && q.options.length > 0
                ? q.options.map((o) => o.optionLabel).filter(Boolean)
                : undefined,
          }))
        : [],
  };
}

/**
 * Adapter: maps the domain entity back to the legacy apiResponseData shape that
 * the remaining wizard steps (cover image, content, automations, checkout) read.
 */
function digitalProductToApiResponse(product: DigitalProduct): CreateProductApiResponse {
  return {
    product: {
      id: product.id,
      userId: product.userId,
      title: product.name,
      slug: product.slug,
      description: product.description,
      coverImage: product.coverImage ?? "",
      cover_images: [],
      hideFromProfile: !product.isVisibleOnProfile(),
      hideVariantFromProfile: product.hideVariantFromProfile,
      request_phone_number: product.requestPhoneNumber,
      purchaseLimit: product.purchaseLimit,
      is_deleted: false,
      statusId: String(product.statusId),
      reference: product.reference,
      url_redirect: product.urlRedirect,
      tiny_url: product.tinyUrl,
      createdAt: product.createdAt,
      updatedAt: product.updatedAt,
      enable_currency_conversion: product.enableCurrencyConversion,
      selected_currencies: product.selectedCurrencies,
    },
  };
}

interface HookResult {
  createProduct(values: CreateProductFormValues): void;
  wasProductCreated: boolean;
  isCreatingProduct: boolean;
  responseData?: CreateProductApiResponse;
}

export function useCreateProductHook(): HookResult {
  const {dispatch} = useCreateProductContext();
  const {
    createProduct: createMutation,
    isCreating,
    createSuccess,
    createdProduct,
  } = useDigitalProductMutations();

  const createProduct = (values: CreateProductFormValues) => {
    createMutation(formValuesToCreateInput(values), {
      onSuccess(product) {
        dispatch({
          type: "save-create-product-api-response",
          payload: {apiResponseData: digitalProductToApiResponse(product)},
        });
        dispatch({
          type: "save-create-product-form-values",
          payload: values,
        });
        dispatch({
          type: "update-create-product-form-step",
          payload: "coverImagesUpload",
        });
      },
      onError(error) {
        message.error(
          error.response?.data?.message || error.message || "Failed to create product"
        );
      },
    });
  };

  return {
    createProduct,
    wasProductCreated: createSuccess,
    isCreatingProduct: isCreating,
    responseData: createdProduct
      ? digitalProductToApiResponse(createdProduct)
      : undefined,
  };
}

interface CreateEditProductHookResult {
  createEditProduct(values: CreateProductFormValues, cb: () => void): void;
  wasProductCreateEdited: boolean;
  isCreateServiceEditing: boolean;
  responseData?: GetProductApiResponse;
}

export function useCreateEditProductHook(): CreateEditProductHookResult {
  const {dispatch, state} = useCreateProductContext();
  const {updateProduct, isUpdating, updateSuccess} = useDigitalProductMutations();

  const createEditProduct = (values: CreateProductFormValues, cb: () => void) => {
    if (state.apiResponseData && state.apiResponseData.product) {
      const input: UpdateDigitalProductInput = {
        ...formValuesToCreateInput(values),
        // Re-submitting step one updates the product the wizard already created, so a
        // cap the coach has since removed has to be cleared rather than left unsaid.
        ...toPurchaseLimitUpdatePayload(values),
        productId: state.apiResponseData.product.id,
      };
      updateProduct(input, {
        onSuccess(product) {
          dispatch({
            type: "save-create-product-api-response",
            payload: {apiResponseData: digitalProductToApiResponse(product)},
          });
          dispatch({
            type: "save-create-product-form-values",
            payload: values,
          });
          cb();
        },
        onError(error) {
          message.error(
            error.response?.data?.message || error.message || "Failed to update product"
          );
        },
      });
      return;
    }
    message.error("No service id found. Go back to step 1");
  };

  return {
    createEditProduct,
    wasProductCreateEdited: updateSuccess,
    isCreateServiceEditing: isUpdating,
  };
}
